import DOMPurify from 'dompurify';
import { emPagination, emLoadMore } from '../../../js/theme/pagination';
import { getParams, setParams } from './params';
import { setActiveFilters } from './active-filters';
import { emFetchPosts, scrollToEl, headerOffsetHeight } from '../../../js/theme/helpers';

/**
 * Pass parameters and fetch from post factory api.
 * @param  {Object}   element  the element to append/replace the html.
 * @param  {Object}   params   the query parameters to pass to the rest api.
 * @param  {Function} template the html markup.
 */
const getFilterableContent = (element, params = {}) => {
	(async () => {
		const hasUrlParams = window.location.search;
		if (hasUrlParams) {
			params.term_counts = true;
		} else {
			// Need this for popstate when no url params.
			params.paged = 1;
		}

		// For load_more pagination: only on initial hydration (empty container) when paged > 1
		if (
			params.pagination === 'load_more'
			&& parseInt(params.paged) > 1
			&& !element.children.length
		) {
			const currentPage = parseInt(params.paged);
			let allHtml = '';

			// Fetch all pages from 1 to current page (sequential – order matters)
			for (let page = 1; page <= currentPage; page++) {
				const pageParams = { ...params, paged: page };
				if (page === 1) {
					pageParams.term_counts = true;
				}
				// eslint-disable-next-line no-await-in-loop -- Sequential fetch required for cumulative hydration
				const pageData = await emFetchPosts(pageParams);

				if (pageData.posts) {
					const isFirstPage = page === 1;
					for (let i = 0; i < pageData.posts.length; i++) {
						const post = pageData.posts[i];
						const sanitizedPost = DOMPurify.sanitize(post);
						const isFirstFeatured = pageData.hasFeatured && i === 0 && isFirstPage;
						allHtml += isFirstFeatured
							? `<div class="em-col featured">${sanitizedPost}</div>`
							: `<div class="em-col">${sanitizedPost}</div>`;
					}
				}
			}

			// Use the last page's data for metadata
			const finalParams = { ...params, paged: currentPage, term_counts: true };
			const data = await emFetchPosts(finalParams);
			const {
				notFoundMessage, blockId, foundPosts, terms,
			} = data;

			const updateTermCountsWithGuards = (termCounts, inputs) => {
				inputs.forEach((input) => {
					const termId = input.getAttribute('data-term-id');
					if (termId) {
						const container = input.closest('.cat-item');
						const count = input.nextElementSibling?.querySelector('.count');
						const disable = () => {
							if (!container) return;
							container.classList.add('disabled');
							input.setAttribute('disabled', 'disabled');
							if (count) count.innerHTML = '(0)';
						};
						const enable = (countNumber) => {
							if (!container) return;
							container.classList.remove('disabled');
							input.removeAttribute('disabled');
							if (count) count.innerHTML = `(${DOMPurify.sanitize(countNumber)})`;
						};
						if (!termCounts) {
							enable(input.dataset.count);
						} else if (termId in termCounts) {
							if (termCounts[termId] === 0) {
								disable();
							} else {
								enable(termCounts[termId]);
							}
						} else {
							disable();
						}
					}
				});
			};

			if (allHtml === '') {
				element.innerHTML = DOMPurify.sanitize(notFoundMessage || '');
				const block = document.getElementById(blockId);
				if (block) {
					const resultCount = block.querySelector(
						'.wp-block-embark-filterable-content__count .count',
					);
					if (resultCount) resultCount.innerHTML = DOMPurify.sanitize('0');
					const inputs = block.querySelectorAll('input');
					setActiveFilters(block, inputs, getFilterableContent);
					updateTermCountsWithGuards(terms, inputs);
				}
				return;
			}

			// Render accumulated content; metadata comes from final fetch even if that page has no posts
			if (allHtml) {
				const { pagination } = data;
				const block = document.getElementById(blockId);

				// Add pagination
				const paginationHtml = emLoadMore(data.maxNumPages, currentPage);
				if (paginationHtml) {
					allHtml += `<div class="em-col-12">${DOMPurify.sanitize(
						paginationHtml,
					)}</div>`;
				}

				element.innerHTML = allHtml;

				// Process the pagination actions.
				const pageNumbers = block.querySelectorAll('.page-numbers, .load-more');
				const filterContainer = block.querySelector(
					'.wp-block-embark-filterable-content__filter-container',
				);
				const contentContainer = block.querySelector(
					'.wp-block-embark-filterable-content__listings',
				);
				const inputs = block.querySelectorAll('input');

				// Loop through page numbers.
				pageNumbers.forEach((page) => {
					page.addEventListener('click', (e) => {
						page = e.target.closest('.page-numbers, .load-more');
						filterContainer.setAttribute('data-paged', page.dataset.page || 1);

						if (pagination !== 'load_more') {
							scrollToEl(element.parentElement, headerOffsetHeight());
						}

						setParams(block);
						// Fetch the content.
						getFilterableContent(contentContainer, getParams(block));
					});
				});

				// Set Count.
				const resultCount = block.querySelector(
					'.wp-block-embark-filterable-content__count .count',
				);
				if (resultCount) {
					resultCount.innerHTML = DOMPurify.sanitize(foundPosts);
				}

				// Set active filter "pills".
				setActiveFilters(block, inputs, getFilterableContent);

				// Update term counts.
				updateTermCountsWithGuards(terms, inputs);
			}

			return; // Exit early after cumulative loading
		}

		// Get the posts
		const data = await emFetchPosts(params);

		if (data.posts) {
			// Get data.
			const {
				blockId,
				posts,
				notFoundMessage,
				foundPosts,
				currentPage,
				pagination,
				terms,
				hasFeatured,
			} = data;

			// Get current block.
			const block = document.getElementById(blockId);

			let html = '';

			// Pass the post data to the template.
			posts.forEach((post, i) => {
				// Sanitize the post content before adding it to HTML
				const sanitizedPost = DOMPurify.sanitize(post);

				// Highlight the first featured post on the first page.
				if (hasFeatured && i === 0 && currentPage === 1) {
					html += `<div class="em-col featured">${sanitizedPost}</div>`;
				} else {
					html += `<div class="em-col">${sanitizedPost}</div>`;
				}
			});

			let paginationHtml = '';

			// Output the pagination.
			if (pagination === 'load_more') {
				const loadMore = block.querySelector('.load-more');
				if (loadMore) {
					const loadMoreParent = loadMore.closest('.em-col-12');
					if (loadMoreParent) {
						loadMoreParent.remove();
					} else {
						loadMore.remove();
					}
				}
				paginationHtml = emLoadMore(data.maxNumPages, currentPage);
			} else {
				paginationHtml = await emPagination(data.maxNumPages, currentPage);
			}

			if (paginationHtml) {
				html += `<div class="em-col-12">${DOMPurify.sanitize(
					paginationHtml,
				)}</div>`;
			}

			// Append or replace the markup.
			if (pagination === 'load_more' && currentPage !== 1) {
				element.innerHTML += html;
			} else {
				element.innerHTML = html;
			}

			// No Posts Found
			if (posts.length === 0) {
				element.innerHTML = DOMPurify.sanitize(notFoundMessage);
			}

			// Process the pagination actions.
			const pageNumbers = block.querySelectorAll('.page-numbers, .load-more');
			const filterContainer = block.querySelector(
				'.wp-block-embark-filterable-content__filter-container',
			);
			const contentContainer = block.querySelector(
				'.wp-block-embark-filterable-content__listings',
			);
			const inputs = block.querySelectorAll('input');

			// Loop through page numbers.
			pageNumbers.forEach((page) => {
				page.addEventListener('click', (e) => {
					page = e.target.closest('.page-numbers, .load-more');
					filterContainer.setAttribute('data-paged', page.dataset.page || 1);

					if (pagination !== 'load_more') {
						scrollToEl(element.parentElement, headerOffsetHeight());
					}

					setParams(block);
					// Fetch the content.
					getFilterableContent(contentContainer, getParams(block));
				});
			});

			// Set Count.
			const resultCount = block.querySelector(
				'.wp-block-embark-filterable-content__count .count',
			);
			if (resultCount) {
				resultCount.innerHTML = DOMPurify.sanitize(foundPosts);
			}

			// Set active filter "pills".
			setActiveFilters(block, inputs, getFilterableContent);

			const updateTermCounts = (terms) => {
				inputs.forEach((input) => {
					const termId = input.getAttribute('data-term-id');
					if (termId) {
						const container = input.closest('.cat-item');
						const count = input.nextElementSibling.querySelector('.count');
						const disable = () => {
							if (!container) return;
							container.classList.add('disabled');
							input.setAttribute('disabled', 'disabled');
							count.innerHTML = '(0)';
						};

						const enable = (countNumber) => {
							if (!container) return;
							container.classList.remove('disabled');
							input.removeAttribute('disabled');
							count.innerHTML = `(${DOMPurify.sanitize(countNumber)})`;
						};

						if (!terms) {
							enable(input.dataset.count);
						} else if (termId in terms) {
							if (terms[termId] === 0) {
								disable();
							} else {
								enable(terms[termId]);
							}
						} else {
							disable();
						}
					}
				});
			};

			//  Update term counts.
			updateTermCounts(terms);

			// Scroll to result only if filter is applied
			if (
				hasUrlParams
				&& embark.breakpoints.below('sm')
				&& ((pagination === 'load_more' && currentPage === 1)
					|| pagination === 'numbered')
			) {
				scrollToEl(element.parentElement, headerOffsetHeight(50));
			}
		}
	})();
};

export default getFilterableContent;
